import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  FileStack,
  Link2,
  RefreshCw,
  ShieldAlert,
  Table2,
} from 'lucide-react'
import './MasterScraperProofTrackerWidget.css'

const TRACKER_PATH = '/data/masterScraperProofTracker.generated.json'
const LIVE_OVERLAP_PATH = '/data/liveOverlapAudit.generated.json'

const STATUS_META = {
  PAIRWISE_CORE_JOIN_PROVEN: { label: 'Pairwise Join', tone: 'proved', rank: 0 },
  CANONICALIZED: { label: 'Canonicalized', tone: 'proved', rank: 1 },
  RAW_ACCOUNTED: { label: 'Raw Accounted', tone: 'proved', rank: 2 },
  RAW_SAMPLE_CAPTURED: { label: 'Captured', tone: 'partial', rank: 3 },
  BLOCKED: { label: 'Blocked', tone: 'blocked', rank: 4 },
  BLOCKED_AUTH: { label: 'Blocked Auth', tone: 'blocked', rank: 4 },
  BLOCKED_CAPTURE: { label: 'Blocked Capture', tone: 'blocked', rank: 4 },
  BUILT_ONLY: { label: 'Built Only', tone: 'unproved', rank: 5 },
}

function formatCount(value) {
  return new Intl.NumberFormat('en-US').format(Number(value || 0))
}

function formatTime(value) {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

function joinLabel(row) {
  if (!Array.isArray(row.pairwise_join_details) || row.pairwise_join_details.length === 0) return '—'
  return row.pairwise_join_details
    .map((pair) => `${pair.partner} ${pair.league} (${pair.shared_core_keys})`)
    .join(' • ')
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.BUILT_ONLY
  const icon =
    meta.tone === 'proved'
      ? <CheckCircle2 size={11} />
      : meta.tone === 'partial'
        ? <AlertTriangle size={11} />
        : meta.tone === 'blocked'
          ? <ShieldAlert size={11} />
          : <FileStack size={11} />

  return <span className={`msp-badge msp-${meta.tone}`}>{icon}{meta.label}</span>
}

export default function MasterScraperProofTrackerWidget() {
  const [tracker, setTracker] = useState(null)
  const [overlap, setOverlap] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [trackerRes, overlapRes] = await Promise.all([
        fetch(TRACKER_PATH).then((response) => (response.ok ? response.json() : null)),
        fetch(LIVE_OVERLAP_PATH).then((response) => (response.ok ? response.json() : null)).catch(() => null),
      ])

      if (!trackerRes) {
        throw new Error('masterScraperProofTracker.generated.json not found')
      }

      setTracker(trackerRes)
      setOverlap(overlapRes)
    } catch (fetchError) {
      setError(fetchError.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const targets = useMemo(() => {
    const rows = Array.isArray(tracker?.targets) ? [...tracker.targets] : []
    return rows.sort((left, right) => {
      const leftMeta = STATUS_META[left.current_status] || STATUS_META.BUILT_ONLY
      const rightMeta = STATUS_META[right.current_status] || STATUS_META.BUILT_ONLY
      if (leftMeta.rank !== rightMeta.rank) return leftMeta.rank - rightMeta.rank
      return left.target.localeCompare(right.target)
    })
  }, [tracker])

  const pairwiseTargets = useMemo(
    () => targets.filter((row) => row.current_status === 'PAIRWISE_CORE_JOIN_PROVEN'),
    [targets]
  )
  const rawAccountedTargets = useMemo(
    () => targets.filter((row) => row.current_status === 'RAW_ACCOUNTED'),
    [targets]
  )
  const canonicalizedTargets = useMemo(
    () => targets.filter((row) => row.current_status === 'CANONICALIZED'),
    [targets]
  )
  const capturedTargets = useMemo(
    () => targets.filter((row) => row.current_status === 'RAW_SAMPLE_CAPTURED'),
    [targets]
  )
  const blockedTargets = useMemo(
    () => targets.filter((row) => String(row.current_status || '').startsWith('BLOCKED')),
    [targets]
  )
  const builtOnlyTargets = useMemo(
    () => targets.filter((row) => row.current_status === 'BUILT_ONLY'),
    [targets]
  )

  const totals = tracker?.totals || {}
  const overlapLeagues = overlap?.leagues || []

  return (
    <div className="widget msp-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          <Table2 size={16} />
          Master Scraper Proof Tracker
        </h3>
        <button className="widget-refresh-btn" onClick={fetchAll} title="Refresh">
          <RefreshCw size={14} className={loading ? 'msp-spinning' : ''} />
        </button>
      </div>

      {error && <div className="msp-error">{error}</div>}

      <div className="msp-summary-grid">
        <div className="msp-summary-card">
          <div className="msp-summary-value">{formatCount(totals.total_targets)}</div>
          <div className="msp-summary-label">Targets</div>
        </div>
        <div className="msp-summary-card">
          <div className="msp-summary-value">{formatCount(totals.raw_accounted_targets)}</div>
          <div className="msp-summary-label">Raw Accounted</div>
        </div>
        <div className="msp-summary-card">
          <div className="msp-summary-value">{formatCount(totals.canonicalized_targets)}</div>
          <div className="msp-summary-label">Canonicalized</div>
        </div>
        <div className="msp-summary-card">
          <div className="msp-summary-value">{formatCount(totals.raw_sample_captured_targets)}</div>
          <div className="msp-summary-label">Captured</div>
        </div>
        <div className="msp-summary-card">
          <div className="msp-summary-value">{formatCount(totals.pairwise_core_join_proven_targets)}</div>
          <div className="msp-summary-label">Pairwise Join</div>
        </div>
        <div className="msp-summary-card">
          <div className="msp-summary-value">{formatCount(totals.blocked_targets)}</div>
          <div className="msp-summary-label">Blocked</div>
        </div>
        <div className="msp-summary-card">
          <div className="msp-summary-value">{formatCount(totals.built_only_targets)}</div>
          <div className="msp-summary-label">Built Only</div>
        </div>
      </div>

      <div className="msp-callout">
        <div className="msp-callout-title">Current truth</div>
        <div className="msp-callout-body">
          Universal all-book join completion is still <strong>{formatCount(totals.universal_all_book_join_complete_targets)}</strong>.
          This widget is the machine-generated proof ledger for what is accounted, captured, blocked, and pairwise-joined right now.
        </div>
      </div>

      {pairwiseTargets.length > 0 && (
        <div className="msp-section">
          <div className="msp-section-title">
            <Link2 size={14} />
            Pairwise Core Join Lane
          </div>
          <div className="msp-table-wrap">
            <table className="msp-table">
              <thead>
                <tr>
                  <th>Target</th>
                  <th>Status</th>
                  <th>Join Pairs</th>
                  <th>Partners / Leagues</th>
                  <th>Next Requirement</th>
                </tr>
              </thead>
              <tbody>
                {pairwiseTargets.map((row) => (
                  <tr key={row.target} className="msp-row-proved">
                    <td className="msp-book-name">{row.target}</td>
                    <td><StatusBadge status={row.current_status} /></td>
                    <td>{formatCount(row.pairwise_core_join_pairs)}</td>
                    <td className="msp-join-label">{joinLabel(row)}</td>
                    <td className="msp-wrap">{row.next_requirement}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {canonicalizedTargets.length > 0 && (
        <div className="msp-section">
          <div className="msp-section-title">
            <Database size={14} />
            Canonicalized Single-Book Lane
          </div>
          <div className="msp-table-wrap">
            <table className="msp-table">
              <thead>
                <tr>
                  <th>Target</th>
                  <th>Status</th>
                  <th>Sports</th>
                  <th>Canonical Events</th>
                  <th>Canonical Offers</th>
                  <th>Join Matrix Rows</th>
                  <th>Next Requirement</th>
                </tr>
              </thead>
              <tbody>
                {canonicalizedTargets.map((row) => (
                  <tr key={row.target} className="msp-row-proved">
                    <td className="msp-book-name">{row.target}</td>
                    <td><StatusBadge status={row.current_status} /></td>
                    <td className="msp-wrap">{row.canonical_sports_covered || '—'}</td>
                    <td>{row.canonical_unique_events ? formatCount(row.canonical_unique_events) : '—'}</td>
                    <td>{row.canonical_unique_offers ? formatCount(row.canonical_unique_offers) : '—'}</td>
                    <td>{row.canonical_join_matrix_rows ? formatCount(row.canonical_join_matrix_rows) : '—'}</td>
                    <td className="msp-wrap">{row.next_requirement}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(capturedTargets.length > 0 || builtOnlyTargets.length > 0 || blockedTargets.length > 0) && (
        <div className="msp-section">
          <div className="msp-section-title">
            <AlertTriangle size={14} />
            Capture / Blocker Lane
          </div>
          <div className="msp-table-wrap">
            <table className="msp-table">
              <thead>
                <tr>
                  <th>Target</th>
                  <th>Status</th>
                  <th>Captured Sports</th>
                  <th>Blocker</th>
                  <th>Next Requirement</th>
                </tr>
              </thead>
              <tbody>
                {[...capturedTargets, ...builtOnlyTargets, ...blockedTargets].map((row) => (
                  <tr key={row.target}>
                    <td className="msp-book-name">{row.target}</td>
                    <td><StatusBadge status={row.current_status} /></td>
                    <td className="msp-wrap">{row.latest_reference_capture_sports || '—'}</td>
                    <td className={row.blocker ? 'msp-red msp-wrap' : 'msp-muted'}>{row.blocker || '—'}</td>
                    <td className="msp-wrap">{row.next_requirement}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {overlapLeagues.length > 0 && (
        <div className="msp-section">
          <div className="msp-section-title">
            <Database size={14} />
            Live Overlap Snapshot
          </div>
          <div className="msp-table-wrap">
            <table className="msp-table">
              <thead>
                <tr>
                  <th>Sport</th>
                  <th>Games</th>
                  <th>Books</th>
                  <th>Max Overlap</th>
                </tr>
              </thead>
              <tbody>
                {overlapLeagues.map((league) => (
                  <tr key={league.league}>
                    <td className="msp-book-name">{league.league}</td>
                    <td>{formatCount(league.game_count)}</td>
                    <td>{formatCount(league.unique_book_count)}</td>
                    <td>{formatCount(league.max_books_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="msp-section">
        <div className="msp-section-title">
          <Database size={14} />
          All Targets
        </div>
        <div className="msp-table-wrap">
          <table className="msp-table">
            <thead>
              <tr>
                <th>Target</th>
                <th>Category</th>
                <th>Status</th>
                <th>Lane</th>
                <th>Raw</th>
                <th>Parsed</th>
                <th>Matched</th>
                <th>Join Pairs</th>
                <th>Blocker</th>
              </tr>
            </thead>
            <tbody>
              {targets.map((row) => (
                <tr key={row.target} className={row.current_status === 'PAIRWISE_CORE_JOIN_PROVEN' ? 'msp-row-proved' : ''}>
                  <td className="msp-book-name">{row.target}</td>
                  <td>{row.category}</td>
                  <td><StatusBadge status={row.current_status} /></td>
                  <td>{row.primary_lane}</td>
                  <td>{row.total_raw_rows ? formatCount(row.total_raw_rows) : '—'}</td>
                  <td>{row.total_parsed_rows ? formatCount(row.total_parsed_rows) : '—'}</td>
                  <td>{row.total_matched_rows ? formatCount(row.total_matched_rows) : '—'}</td>
                  <td>{row.pairwise_core_join_pairs ? formatCount(row.pairwise_core_join_pairs) : '—'}</td>
                  <td className={row.blocker ? 'msp-red msp-wrap' : 'msp-muted'}>{row.blocker || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {loading && !tracker && <div className="msp-empty">Loading tracker data...</div>}
      {!loading && !tracker && !error && <div className="msp-empty">No master proof tracker data found yet.</div>}

      {tracker && (
        <div className="msp-footer">
          Tracker generated: {formatTime(tracker.generated_at)}
          {tracker.source_paths?.master_matrix && <> | Master matrix: {tracker.source_paths.master_matrix}</>}
        </div>
      )}

      {tracker && (
        <div className="msp-footer msp-footer-secondary">
          Raw-accounted targets: {rawAccountedTargets.length} | Captured targets: {capturedTargets.length} | Blocked targets: {blockedTargets.length}
          {canonicalizedTargets.length > 0 && <> | Canonicalized targets: {canonicalizedTargets.length}</>}
        </div>
      )}
    </div>
  )
}
